import { Check, ChevronsUpDown } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SidebarNavBadge } from "@/components/sidebar-nav-badge"
import { WorkspaceTile } from "@/components/workspace-tile"
import { useSupportCounts } from "@/lib/api/support"
import { useWorkspace } from "@/hooks/use-workspace"
import { workspaceMeta, type WorkspaceId } from "@/lib/workspaces"
import { cn } from "@/lib/utils"

/**
 * The workspace switcher at the top of the sidebar, shaped like Resend's org
 * switcher (h-10, rounded-xl, 24px avatar, semibold label). Renders nothing
 * for someone with a single workspace.
 */
export function WorkspaceSwitcher() {
  const { current, available, switchTo } = useWorkspace()
  // Workspaces with something waiting on us. Support: unread or awaiting our
  // reply, in the projects this person can see (the API scopes the count).
  const { data: supportCounts } = useSupportCounts(available.includes("support"))
  const pending: Partial<Record<WorkspaceId, number>> = { support: supportCounts?.needs_action ?? 0 }
  // On the button, only for workspaces other than the open one: a nudge to switch in.
  const elsewhere = available.filter((id) => id !== current).reduce((n, id) => n + (pending[id] ?? 0), 0)
  if (available.length < 2) return null
  const meta = workspaceMeta(current)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="relative flex h-12 w-full cursor-pointer items-center justify-between gap-2 rounded-xl border border-border bg-background px-2 text-left shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition duration-200 ease-in-out hover:border-foreground/20 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:border-foreground/20 data-[state=open]:bg-muted/60 group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:border-transparent group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:shadow-none"
          aria-label={`Workspace: ${meta.label}. Switch workspace${elsewhere ? `, ${elsewhere} waiting elsewhere` : ""}`}
        >
          <span className="flex min-w-0 items-center gap-2.5">
            <WorkspaceTile id={current} size="md" />
            <span className="min-w-0 group-data-[collapsible=icon]:hidden">
              <span className="block text-[10px] font-medium uppercase leading-3 tracking-[0.08em] text-muted-foreground">Workspace</span>
              <span className="block truncate text-sm font-semibold leading-5">{meta.label}</span>
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1.5">
            {elsewhere > 0 && <SidebarNavBadge count={elsewhere} />}
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden" />
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[240px] p-1.5"
        // Picking a workspace navigates away, so handing focus back to the
        // trigger would only leave a stale ring on it.
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel className="px-2 pb-1 pt-0.5 text-xs font-medium text-muted-foreground">Workspaces</DropdownMenuLabel>
        {available.map((id) => {
          const w = workspaceMeta(id)
          const active = id === current
          return (
            <DropdownMenuItem
              key={id}
              onSelect={() => switchTo(id)}
              className={cn("flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5", active && "bg-muted")}
            >
              <WorkspaceTile id={id} size="md" />
              <span className="min-w-0 flex-1 truncate text-[15px] font-semibold">{w.label}</span>
              {!!pending[id] && <SidebarNavBadge count={pending[id]!} />}
              {active && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
